import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Marquee } from "@/components/Marquee";

const IMAGES = [
  "https://images.unsplash.com/photo-1532332248682-206cc786359f?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1576775068668-c147f14c36f7?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1562157873-818bc0726f68?crop=entropy&cs=srgb&fm=jpg&q=85",
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

export default function AboutPage() {
  return (
    <main data-testid="about-page" className="pt-16">
      <Seo
        title="About the Studio — Marabou Design"
        description="Marabou Design is an independent T-shirt studio building garments with the permanence of architecture: heavyweight organic cotton, zero-waste inks, numbered drops."
      />
      <section className="border-b border-[#E4E4DE] py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#4D7C0F]/90">The manifesto</p>
          <motion.h1
            className="mt-6 max-w-4xl font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold uppercase tracking-tighter leading-[0.9]"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            Built like brutalism. <span className="text-[#4D7C0F]">Worn like armor.</span>
          </motion.h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24 grid gap-12 lg:grid-cols-2">
        <motion.div {...fadeUp}>
          <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">
            We started with a simple frustration
          </h2>
          <p className="mt-6 text-sm sm:text-base text-zinc-500 leading-relaxed">
            Every tee on the market felt disposable — thin fabric, prints that peel after
            three washes, fits designed by spreadsheet. Marabou Design was founded in an
            Antwerp workshop in 2021 to build the opposite: garments with the permanence
            of architecture.
          </p>
          <p className="mt-4 text-sm sm:text-base text-zinc-500 leading-relaxed">
            We source 100% organic heavyweight cotton from a single Portuguese mill, cut
            every pattern in-house, and print with zero-waste water-based inks that are
            engineered to age — cracking and fading like concrete, gaining character
            with every wear.
          </p>
        </motion.div>
        <motion.div {...fadeUp} className="border border-[#E4E4DE]">
          <img src={IMAGES[0]} alt="Kinetik Archive studio" className="h-full w-full object-cover aspect-[4/3]" />
        </motion.div>
      </section>

      <Marquee />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24">
        <motion.h2 {...fadeUp} className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">
          The process, in numbers
        </motion.h2>
        <div className="mt-10 grid gap-px bg-[#E4E4DE] sm:grid-cols-2 lg:grid-cols-4 border border-[#E4E4DE]">
          {[
            ["280GSM", "Minimum fabric weight across every garment we ship"],
            ["04", "Numbered drops per year — never restocked twice"],
            ["00", "Liters of water wasted in our print process"],
            ["48H", "From your order to the courier's hands"],
          ].map(([n, label], i) => (
            <motion.div
              key={n}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="bg-[#F7F7F2] p-8"
            >
              <p className="font-display text-4xl font-extrabold text-[#4D7C0F]">{n}</p>
              <p className="mt-4 font-mono text-xs uppercase tracking-widest text-zinc-500 leading-relaxed">{label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-t border-[#E4E4DE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24 grid gap-8 lg:grid-cols-3">
          {IMAGES.map((img, i) => (
            <motion.div
              key={img}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.1 }}
              className={`overflow-hidden border border-[#E4E4DE] ${i === 1 ? "lg:mt-12" : ""}`}
            >
              <img src={img} alt="Kinetik Archive lookbook" className="w-full aspect-[3/4] object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
          ))}
        </div>
        <div className="pb-20 text-center">
          <p className="mx-auto max-w-2xl px-4 font-display text-xl sm:text-2xl font-bold uppercase tracking-tight text-zinc-600">
            "The heaviest tee we've ever reviewed. It drapes like poured concrete — in the best way possible."
          </p>
          <p className="mt-4 font-mono text-xs uppercase tracking-widest text-zinc-600">— Streetform Magazine</p>
          <Link
            to="/shop"
            data-testid="about-shop-cta"
            className="mt-10 inline-flex items-center gap-2 bg-[#C8FF1E] px-10 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black hover:bg-[#B0E600] hover:text-black transition-colors"
          >
            Shop the archive <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
